import React, { useState } from 'react';
import { Building2, Home, Shield, Rocket, Headphones, TrendingUp, MapPin } from 'lucide-react';
import useInView from '../hooks/useInView';

const USE_CASES = [
  {
    icon: Building2,
    accent: 'accent-1',
    label: 'BPO / KPO',
    headline: 'Cut infrastructure costs by 60%',
    body: 'Run a full-scale outbound BPO without EPABX, servers or desk phones. Deploy new agents in hours, not weeks. Scale up for project peaks, scale down when they end — pay only for what you use.',
    bullets: ['No hardware procurement', 'Agent onboarding in under an hour', 'Multi-campaign management', 'Real-time supervisor view'],
  },
  {
    icon: Home,
    accent: 'accent-3',
    label: 'Real estate',
    headline: 'Follow up faster, close more deals',
    body: 'Your sales team is always on the move. WASLX lets field agents call, log notes and set reminders from their phone without opening a laptop. The auto dialer burns through lead lists while you are showing a property.',
    bullets: ['Field-agent friendly', 'Auto dialer for lead lists', 'Instant follow-up reminders', 'Call recordings for compliance'],
  },
  {
    icon: Shield,
    accent: 'accent-4',
    label: 'Insurance / loans',
    headline: 'Compliant calls, complete records',
    body: 'Every call recorded. Every lead tracked. Every agent monitored. A full audit trail for regulatory requirements, without the complexity of enterprise telecom.',
    bullets: ['100% call recording', 'Lead disposition tracking', 'Agent performance scoring', 'Campaign-level reporting'],
  },
  {
    icon: Rocket,
    accent: 'accent-5',
    label: 'Startups',
    headline: 'Launch call ops today, not next quarter',
    body: 'Skip the procurement headache. No vendor negotiations, no hardware delivery, no IT setup. WASLX gives you serious calling infrastructure from day one, at startup pricing.',
    bullets: ['Zero upfront cost', 'No contracts required', 'Start with 1 agent, scale to 1000', '30-minute onboarding'],
  },
  {
    icon: TrendingUp,
    accent: 'accent-6',
    label: 'Sales teams',
    headline: 'More talk time. More pipeline.',
    body: 'The auto dialer eliminates manual dialing and skip rates, so agents spend far more of the day talking to prospects. CRM sync means no double entry, and real-time coaching means faster ramp.',
    bullets: ['3× more talk time', 'Auto lead rotation', 'Live supervisor whisper', 'Pipeline analytics'],
  },
  {
    icon: MapPin,
    accent: 'accent-7',
    label: 'Banking / field sales',
    headline: 'Track every field agent. Close every lead.',
    body: 'The call center books a KYC appointment, the nearest field agent is auto-assigned and goes live on the map, documents are scanned on site and submitted through the portal. Team leads see every agent’s location and interaction log in real time.',
    bullets: ['Auto-assign leads by GPS proximity', 'Team-lead allocation dashboard', 'Live location map for field agents', 'On-site document scan & submission'],
  },
  {
    icon: Headphones,
    accent: 'accent-8',
    label: 'Customer support',
    headline: 'Remote-ready support operations',
    body: 'Support agents handle outbound follow-ups, callbacks and proactive outreach from home. Live monitoring keeps quality high even when the team is fully distributed.',
    bullets: ['Remote-first by design', 'Quality monitoring tools', 'Callback scheduling', 'Customer history on call'],
  },
];

const UseCasesSection = () => {
  const [active, setActive] = useState(0);
  const [ref, inView] = useInView();
  const uc = USE_CASES[active];
  const ActiveIcon = uc.icon;

  return (
    <section ref={ref} className={`section section--surface reveal ${inView ? 'is-inview' : ''}`}>
      <div className="container-page">
        <div className="section-head">
          <span className="eyebrow accent-5">Use cases</span>
          <h2 className="section-title">
            Built for your <span className="display-accent">industry</span>
          </h2>
          <p className="section-sub">
            Whether you run a BPO or a five-person sales team, WASLX fits how you work.
          </p>
        </div>

        <div className="grid gap-5 lg:grid-cols-5">
          <div className="flex flex-col gap-1.5 lg:col-span-2" role="tablist" aria-label="Industries">
            {USE_CASES.map((item, i) => {
              const Icon = item.icon;
              const isActive = active === i;
              return (
                <button
                  key={item.label}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(i)}
                  className={`focus-ring uc-tab ${item.accent} ${isActive ? 'is-active' : ''}`}
                >
                  <span className={`icon-tile icon-tile--sm ${isActive ? 'icon-tile--solid' : ''}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>

          <div className={`${uc.accent} card card--topbar lg:col-span-3`}>
            {/* Keyed on `active`, so switching industry re-runs the fade and
                the panel reads as replaced rather than silently rewritten. */}
            <div key={active} className="animate-fade-in">
            <div className="mb-5 flex items-center gap-3">
              <span className="icon-tile">
                <ActiveIcon className="h-5 w-5" />
              </span>
              <span className="chip">{uc.label}</span>
            </div>

            <h3 className="text-2xl font-bold leading-tight text-content">{uc.headline}</h3>
            <p className="mt-4 leading-relaxed text-content-muted">{uc.body}</p>

            <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
              {uc.bullets.map(bullet => (
                <li key={bullet} className="flex items-start gap-2.5 text-[0.9375rem] text-content">
                  <span className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-pill bg-accent" />
                  {bullet}
                </li>
              ))}
            </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
